import { FileItem, FolderRef } from '../types';
import { folderRefForEntry } from '../navigationModel';

interface UseFileNavigationProps {
  onNavigate: (folder: FolderRef) => void;
}

/**
 * Navigation intents, as a thin wrapper over the pure `navigationModel`
 * transitions. Every destination is a `FolderRef` — the node, and since B-4
 * nothing but the node (STD-029 R-10).
 *
 * `navigateToPath(index)` is gone with the path half of the ref. A breadcrumb
 * carries its own node ref now (`crumbRefs`), resolved from the server's
 * ancestor chain, so navigating to one is `navigateTo(crumb.ref)` and needs no
 * index arithmetic over a path string. That arithmetic is precisely what could
 * only ever describe the caller's own tree — inside a share there is no path to
 * slice.
 */
export const useFileNavigation = ({ onNavigate }: UseFileNavigationProps) => {
  const navigateTo = (folder: FolderRef) => {
    onNavigate(folder);
  };

  const navigateToFile = (file: FileItem) => {
    if (file.isFolder) {
      onNavigate(folderRefForEntry(file));
    }
  };

  // `navigateToHome` was deleted here, and its absence is the point rather than
  // tidiness. It returned `ROOT_FOLDER` — `{id: null}` — and B-4 orphaned it by
  // giving the breadcrumb real nodes: every crumb, the Home one included, now
  // carries `node.object_id`, so `navigateTo(crumb.ref)` covers it. Left in
  // place it would have been a live route back to the one ref value that ALIASES
  // the organization root, which is precisely what the clipboard-origin bug
  // needed to fire (see `useFileClipboard.cutFrom`).
  return {
    navigateTo,
    navigateToFile,
  };
};
